import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  FormControlLabel, 
  Switch, 
  CircularProgress, 
  Alert, 
  Divider,
  Tabs,
  Tab,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Save as SaveIcon, ArrowBack as BackIcon, Preview as PreviewIcon, AttachFile as AttachFileIcon, Delete as DeleteIcon } from '@mui/icons-material';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import emailTemplateService, { EmailTemplate, EmailTemplateAttachment } from '../../services/emailTemplateService';

// Interface for form errors
interface FormErrors {
  name?: string;
  subject?: string;
  content?: string;
}

// Interface for template form data (without required fields like id, timestamps)
interface EmailTemplateFormData {
  name: string;
  subject: string;
  content: string;
  is_active: boolean;
}

// Define the tab interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-template-tabpanel-${index}`}
      aria-labelledby={`email-template-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Main component
const EmailTemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNewTemplate = !id || id === 'new';
  const isEditMode = !isNewTemplate;
  const navigate = useNavigate();
  
  // Define the state using form data interface
  const [template, setTemplate] = useState<EmailTemplateFormData>({
    name: '',
    subject: '',
    content: '',
    is_active: true
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [templateVars, setTemplateVars] = useState<Array<{ name: string; description: string }>>([]);
  const [attachments, setAttachments] = useState<EmailTemplateAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  useEffect(() => {
    emailTemplateService.getTemplateVariables().then((res) => {
      if (res?.data) setTemplateVars(res.data);
    }).catch(() => {});
  }, []);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };
  
  // Fetch template data if in edit mode
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        console.log(`Fetching template with ID: ${id}`);
        const data = await emailTemplateService.getById(parseInt(id!));
        console.log('Fetched template data:', data);
        setTemplate(data);
        setAttachments(data.attachments || []);
      } catch (err) {
        console.error('Failed to fetch template:', err);
        setSaveError('Failed to load email template. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplate();
  }, [id, isEditMode]);

  // Handle field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'is_active') {
      setTemplate(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setTemplate(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear errors for the field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle content change from the rich text editor
  const handleContentChange = (content: string) => {
    setTemplate(prev => ({ ...prev, content }));
    
    // Clear content error if exists
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: undefined }));
    }
  };

  // Validate the form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!template.name.trim()) {
      newErrors.name = 'Template name is required';
    }
    
    if (!template.subject.trim()) {
      newErrors.subject = 'Email subject is required';
    }
    
    if (!template.content.trim() || template.content === '<p></p>' || template.content === '<br>') {
      newErrors.content = 'Email content is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setSaveError(null);
      
      console.log(`Template submission - isEditMode: ${isEditMode}, id: ${id}`);
      console.log('Template data to submit:', template);
      
      let result;
      if (isEditMode) {
        // Update existing template
        console.log(`Updating template with ID: ${id}`);
        result = await emailTemplateService.updateEmailTemplate(parseInt(id!), template);
        console.log('Updated template:', result);
      } else {
        // Create new template
        console.log('Creating new template');
        result = await emailTemplateService.createEmailTemplate(template);
        console.log('Created template:', result);
      }

      if (!isEditMode && result?.id) {
        navigate(`/email-templates/edit/${result.id}`);
        return;
      }

      navigate('/email-templates');
    } catch (err: any) {
      console.error('Failed to save template:', err);
      // Extract more detailed error information
      let errorMessage = 'Failed to save email template. Please try again later.';
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        if (err.response.status === 401) {
          errorMessage = 'Authentication error. Please log in again.';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      }
      setSaveError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle preview
  const handlePreview = () => {
    // Switch to preview tab
    setTabIndex(1);
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/email-templates');
  };

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !isEditMode || !id) return;

    try {
      setUploadingAttachment(true);
      setAttachmentError(null);
      const uploaded = await emailTemplateService.uploadAttachment(parseInt(id), file);
      setAttachments((prev) => [...prev, uploaded]);
    } catch (err: any) {
      console.error('Failed to upload attachment:', err);
      setAttachmentError(
        err.response?.data?.message || 'Failed to upload attachment. Please try again.'
      );
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleAttachmentDelete = async (attachmentId: number) => {
    if (!isEditMode || !id) return;

    try {
      setDeletingAttachmentId(attachmentId);
      setAttachmentError(null);
      await emailTemplateService.deleteAttachment(parseInt(id), attachmentId);
      setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
    } catch (err: any) {
      console.error('Failed to delete attachment:', err);
      setAttachmentError(
        err.response?.data?.message || 'Failed to delete attachment. Please try again.'
      );
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  if (loading && isEditMode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Edit Email Template' : 'Create New Email Template'}
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<BackIcon />}
          onClick={handleCancel}
        >
          Back to List
        </Button>
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Template name and status */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 300px' }}>
                <TextField
                  fullWidth
                  label="Template Name"
                  name="name"
                  value={template.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Box>
              <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={template.is_active}
                      onChange={handleChange as React.ChangeEventHandler<HTMLInputElement>}
                      name="is_active"
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>

            {/* Email subject */}
            <Box>
              <TextField
                fullWidth
                label="Email Subject"
                name="subject"
                value={template.subject}
                onChange={handleChange}
                error={!!errors.subject}
                helperText={errors.subject}
                required
              />
            </Box>

            {templateVars.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Insert variables (click to append to subject)
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {templateVars.map((v) => (
                    <Chip
                      key={v.name}
                      size="small"
                      label={`{{ ${v.name} }}`}
                      title={v.description}
                      onClick={() =>
                        setTemplate((prev) => ({
                          ...prev,
                          subject: `${prev.subject} {{ ${v.name} }}`,
                        }))
                      }
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  In the HTML body, type the same placeholders, e.g. {'{{ guest_name }}'}.
                </Typography>
              </Box>
            )}

            {/* Attachments */}
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Email Attachments
              </Typography>
              {!isEditMode ? (
                <Typography variant="body2" color="text.secondary">
                  Save the template first, then you can add file attachments that will be sent with every email using this template.
                </Typography>
              ) : (
                <>
                  {attachmentError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {attachmentError}
                    </Alert>
                  )}
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFileIcon />}
                      disabled={uploadingAttachment}
                    >
                      {uploadingAttachment ? 'Uploading...' : 'Add Attachment'}
                      <input
                        type="file"
                        hidden
                        onChange={handleAttachmentUpload}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Max 10 MB per file. Attachments are included in campaigns, test emails, and other sends using this template.
                    </Typography>
                  </Stack>
                  {attachments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No attachments added yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {attachments.map((attachment) => (
                        <Paper
                          key={attachment.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography variant="body2">{attachment.filename}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(attachment.file_size)}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {attachment.file_url && (
                              <Button
                                size="small"
                                href={attachment.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Download
                              </Button>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleAttachmentDelete(attachment.id)}
                              disabled={deletingAttachmentId === attachment.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Box>

            {/* Email content section */}
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Email Content
              </Typography>
            </Box>

            {/* Editor/Preview tabs */}
            <Box>
              <Tabs value={tabIndex} onChange={handleTabChange}>
                <Tab label="Editor" />
                <Tab label="Preview" />
              </Tabs>
              
              <TabPanel value={tabIndex} index={0}>
                <RichTextEditor
                  value={template.content}
                  onChange={handleContentChange}
                  placeholder="Type your email content here..."
                  error={!!errors.content}
                />
                {errors.content && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {errors.content}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Use the formatting controls above to style your email content.
                </Typography>
              </TabPanel>
              
              <TabPanel value={tabIndex} index={1}>
                <Paper sx={{ p: 3, border: '1px solid #ccc', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Subject: {template.subject}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box 
                    sx={{ 
                      p: 2, 
                      minHeight: '300px',
                      '& img': { maxWidth: '100%' } // Make images responsive
                    }}
                    dangerouslySetInnerHTML={{ __html: template.content }}
                  />
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  This is how the email will appear to recipients. Return to the Editor tab to make changes.
                </Typography>
              </TabPanel>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<PreviewIcon />} 
                onClick={handlePreview}
              >
                Preview
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Template'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default EmailTemplateEditor; 